import type {
  FinishIncidentRequest,
  Patrol,
  RecordIncidentRequest,
} from '@local/common-lib';
import geocodingService from '@mapbox/mapbox-sdk/services/geocoding.js';
import * as Sentry from '@sentry/react';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useState } from 'react';
import { useAsyncGeolocation } from '../../../hooks/use-async-geolocation.js';
import { useAuthenticated } from '../../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../../utils/types.js';
import { FinishPatrolContent } from './finish.controller.js';
import { PatrolProgressView } from './progress.view.js';

const geocoding = import.meta.env.VITE_APP_MAPBOX_TOKEN
  ? geocodingService({ accessToken: import.meta.env.VITE_APP_MAPBOX_TOKEN })
  : undefined;

export const PatrolProgressContent: FC<
  ExistingEntityViewProps<Patrol.Type>
> = ({ data: patrol, id, refresh, fetchNow }) => {
  const auth = useAuthenticated();
  const { enqueueSnackbar } = useSnackbar();
  const [showAdd, setShowAdd] = useState(false);
  const [showFinish, setShowFinish] = useState(false);
  const [finishingIncidentId, setFinishingIncidentId] = useState<string>();
  const [locationLoading, setLocationLoading] = useState(false);
  const { getPosition, isGeolocationAvailable, isGeolocationEnabled } =
    useAsyncGeolocation({
      watchLocationPermissionChange: true,
    });

  const onAddLocation = (): void => {
    if (!isGeolocationEnabled) {
      enqueueSnackbar('Location disabled', { variant: 'warning' });
      return;
    }
    if (!isGeolocationAvailable) {
      enqueueSnackbar('Location unavailable', { variant: 'warning' });
      return;
    }

    setLocationLoading(true);
    getPosition()
      .then(({ coords, timestamp }) => {
        if (!coords?.latitude || !coords.longitude) return;

        if (!geocoding) {
          return {
            location: {
              latitude: coords.latitude,
              longitude: coords.longitude,
            },
            when: timestamp,
          };
        }

        return geocoding
          .reverseGeocode({
            limit: 1,
            query: [coords.longitude, coords.latitude],
          })
          .send()
          .then((result) => ({
            location: {
              address: result?.body?.features[0].place_name,
              latitude: coords.latitude,
              longitude: coords.longitude,
            },
            when: timestamp,
          }));
      })
      .then(async (result) => {
        if (!result) return;

        const body: RecordIncidentRequest = {
          location: result.location,
          people: undefined,
          peopleDetails: undefined,
          what: 'Location',
          whatDetails: undefined,
          when: DateTime.fromMillis(result.when).toJSDate(),
        };

        return auth.axios.post(
          `/api/v1/patrol/${encodeURIComponent(id)}/incident`,
          body,
        );
      })
      .then(() => {
        enqueueSnackbar('Location recorded', { variant: 'success' });
        setShowAdd(false);
        refresh();
        return undefined;
      })
      .catch((err) => {
        Sentry.captureException(err);
        if (err instanceof Error) {
          enqueueSnackbar(`Unable to record incident: ${err.message}`, {
            variant: 'error',
          });
        } else {
          throw err;
        }
        refresh();
      })
      .finally(() => {
        setLocationLoading(false);
      })
      .catch((err) => {
        Sentry.captureException(err);
      });
  };

  const onFinishIncident = (incident: Patrol.Incident): void => {
    setFinishingIncidentId(incident._id);
    const body: FinishIncidentRequest = {
      whenFinished: new Date(),
    };

    auth.axios
      .post(
        `/api/v1/patrol/${encodeURIComponent(id)}/incident/${encodeURIComponent(incident._id)}/finish`,
        body,
      )
      .then(() => {
        enqueueSnackbar('Incident end time recorded', { variant: 'success' });
        refresh();
        return undefined;
      })
      .catch((err) => {
        Sentry.captureException(err);
        if (err instanceof Error) {
          enqueueSnackbar(`Unable to finish incident: ${err.message}`, {
            variant: 'error',
          });
        } else {
          throw err;
        }
        refresh();
      })
      .finally(() => {
        setFinishingIncidentId(undefined);
      })
      .catch((err) => {
        Sentry.captureException(err);
      });
  };

  if (showFinish) {
    return (
      <FinishPatrolContent
        data={patrol}
        fetchNow={fetchNow}
        id={id}
        refresh={refresh}
        setShowFinish={setShowFinish}
      />
    );
  }

  return (
    <PatrolProgressView
      finishingIncidentId={finishingIncidentId}
      locationAvailable={isGeolocationEnabled && isGeolocationAvailable}
      locationLoading={locationLoading}
      onAddLocation={onAddLocation}
      onFinishIncident={onFinishIncident}
      patrol={patrol}
      refresh={refresh}
      setShowAdd={setShowAdd}
      setShowFinish={setShowFinish}
      showAdd={showAdd}
    />
  );
};
